import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ChefHat,
  Clock,
  Flame,
  ShoppingCart,
  Download,
  ArrowLeft,
  Utensils,
  ChevronsDownUp,
  ChevronsUpDown,
  RefreshCw,
  ArrowRightCircle,
  CheckCircle2,
  CalendarPlus
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import ShoppingListModal from '../components/ShoppingListModal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Meal {
  name: string;
  ingredients: string[];
  cooking_method: string;
  calories: number;
  protein: number;
  benefits: string;
  preparation?: string;
}

interface Day {
  day: number;
  breakfast: Meal;
  lunch: Meal;
  dinner: Meal;
}

interface MealPlanData {
  days: Day[];
  shopping_list: string[];
  recommendations: string;
}

export default function MealPlanDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const plan = location.state?.plan;

  // ── Toate hook-urile ÎNAINTE de orice return condiționat ──────────────────
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1]));
  const [exportingPDF, setExportingPDF] = useState(false);
  const [regeneratingDay, setRegeneratingDay] = useState<number | null>(null);
  const [transferringDay, setTransferringDay] = useState<number | null>(null);
  const [transferredDays, setTransferredDays] = useState<Set<number>>(new Set());
  const [currentPlanData, setCurrentPlanData] = useState<MealPlanData>(plan?.data ?? { days: [], shopping_list: [], recommendations: '' });
  const [showTransferModal, setShowTransferModal] = useState<{ dayNumber: number } | null>(null);
  const [transferDate, setTransferDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [showShoppingList, setShowShoppingList] = useState(false);

  const allExpanded = plan?.data?.days ? expandedDays.size === plan.data.days.length : false;

  const toggleDay = (day: number) => {
    setExpandedDays(prev => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  const toggleAllDays = () => {
    if (allExpanded) {
      setExpandedDays(new Set());
    } else {
      const allDayNumbers = plan.data.days.map((d: Day) => d.day);
      setExpandedDays(new Set(allDayNumbers));
    }
  };

  // ── Guard: plan lipsă ─────────────────────────────────────────────────────
  if (!plan) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Plan nu a fost găsit</p>
          <button
            onClick={() => navigate('/nutrition')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Înapoi la Nutriție
          </button>
        </div>
      </div>
    );
  }

  const planData: MealPlanData = plan.data;

  const handleRegenerateDay = async (dayNumber: number) => {
    if (!confirm(`Regenerezi ziua ${dayNumber}? Mesele actuale vor fi înlocuite.`)) return;
    setRegeneratingDay(dayNumber);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/nutrition/meal-plan/${plan.id}/regenerate-day`,
        { dayNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Update local plan data
      setCurrentPlanData(response.data.data);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Eroare la regenerare. Încearcă din nou.';
      alert(`❌ ${msg}`);
    } finally {
      setRegeneratingDay(null);
    }
  };

  const handleTransferDay = async (dayNumber: number, date: string) => {
    setTransferringDay(dayNumber);
    setShowTransferModal(null);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_URL}/api/nutrition/meal-plan/${plan.id}/transfer-to-tracking`,
        { dayNumber, date },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { transferred } = response.data;
      setTransferredDays(prev => new Set([...prev, dayNumber]));
      alert(`✅ ${transferred} mese transferate în Mese & Calorii pentru ${new Date(date + 'T12:00:00').toLocaleDateString('ro-RO')}!`);
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Eroare la transfer. Încearcă din nou.';
      alert(`❌ ${msg}`);
    } finally {
      setTransferringDay(null);
    }
  };

  const handleExportPDF = async () => {
    const planData2: MealPlanData = currentPlanData;
    const dateStr = new Date(plan.createdAt).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long', year: 'numeric' });
    const fileName = `plan_nutritional_${plan.days}_zile_${new Date().toISOString().split('T')[0]}.pdf`;

    setExportingPDF(true);

    // Helper: randează un fragment HTML într-un div ascuns și returnează canvas-ul
    const renderBlockToCanvas = async (innerHtml: string): Promise<HTMLCanvasElement> => {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.top = '-99999px';
      wrapper.style.left = '-99999px';
      wrapper.style.width = '754px'; // 794px - 2*20px padding
      wrapper.style.background = '#f8fafc';
      wrapper.style.fontFamily = 'Arial, Helvetica, sans-serif';
      wrapper.style.color = '#111827';
      wrapper.innerHTML = innerHtml;
      document.body.appendChild(wrapper);
      await new Promise(r => setTimeout(r, 100));
      const c = await html2canvas(wrapper, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#f8fafc',
        width: 754,
        windowWidth: 754,
      });
      document.body.removeChild(wrapper);
      return c;
    };

    try {
      const totalCal = planData2.days.reduce((s, d) => s + d.breakfast.calories + d.lunch.calories + d.dinner.calories, 0);
      const avgCal = Math.round(totalCal / planData2.days.length);

      // ── HTML fragments ──────────────────────────────────────────────────────

      const headerHtml = `
        <div style="background:linear-gradient(135deg,#16a34a,#2563eb);border-radius:16px;padding:24px;color:#fff;text-align:center;">
          <div style="font-size:32px;margin-bottom:6px;">🥗</div>
          <h1 style="font-size:20px;font-weight:800;margin-bottom:4px;">Plan Nutrițional Personalizat</h1>
          <p style="font-size:12px;opacity:0.9;margin-bottom:12px;">${plan.days} Zile · Generat pe ${dateStr}</p>
          <div style="display:flex;justify-content:center;gap:12px;flex-wrap:wrap;">
            <div style="background:rgba(255,255,255,0.2);border-radius:10px;padding:8px 14px;text-align:center;">
              <div style="font-size:18px;font-weight:800;">${plan.days}</div>
              <div style="font-size:10px;opacity:0.85;">Zile</div>
            </div>
            <div style="background:rgba(255,255,255,0.2);border-radius:10px;padding:8px 14px;text-align:center;">
              <div style="font-size:18px;font-weight:800;">${avgCal}</div>
              <div style="font-size:10px;opacity:0.85;">kcal/zi medie</div>
            </div>
            <div style="background:rgba(255,255,255,0.2);border-radius:10px;padding:8px 14px;text-align:center;">
              <div style="font-size:18px;font-weight:800;">${planData2.days.length * 3}</div>
              <div style="font-size:10px;opacity:0.85;">Mese totale</div>
            </div>
            <div style="background:rgba(255,255,255,0.2);border-radius:10px;padding:8px 14px;text-align:center;">
              <div style="font-size:18px;font-weight:800;">${planData2.shopping_list?.length || 0}</div>
              <div style="font-size:10px;opacity:0.85;">Ingrediente</div>
            </div>
          </div>
        </div>`;

      const recHtml = planData2.recommendations ? `
        <div style="background:linear-gradient(135deg,#f0fdf4,#eff6ff);border-radius:12px;padding:16px;border:1px solid #bbf7d0;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <span style="font-size:16px;">💡</span>
            <span style="font-size:13px;font-weight:700;color:#14532d;">Recomandări Generale</span>
          </div>
          <p style="font-size:11px;color:#374151;line-height:1.6;margin:0;">${planData2.recommendations}</p>
        </div>` : '';

      const shoppingHtml = planData2.shopping_list && planData2.shopping_list.length > 0 ? `
        <div style="background:#fff;border-radius:12px;box-shadow:0 2px 6px rgba(0,0,0,0.07);padding:16px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
            <span style="font-size:18px;">🛒</span>
            <span style="font-size:14px;font-weight:700;color:#111827;">Listă de Cumpărături</span>
          </div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:5px;">
            ${planData2.shopping_list.map(item => `
              <div style="display:flex;align-items:center;gap:5px;background:#eff6ff;border-radius:6px;padding:5px 7px;">
                <div style="width:5px;height:5px;background:#2563eb;border-radius:50%;flex-shrink:0;"></div>
                <span style="font-size:10px;color:#1e3a5f;">${item}</span>
              </div>`).join('')}
          </div>
        </div>` : '';

      const mealBlockHtml = (meal: Meal, type: string, emoji: string) => `
        <div style="background:#f0fdf4;border-radius:9px;padding:12px;margin-bottom:8px;border-left:4px solid #16a34a;">
          <div style="display:flex;align-items:center;gap:7px;margin-bottom:7px;">
            <span style="font-size:16px;">${emoji}</span>
            <div>
              <div style="font-size:9px;color:#6b7280;text-transform:uppercase;letter-spacing:0.05em;">${type}</div>
              <div style="font-size:12px;font-weight:700;color:#111827;">${meal.name}</div>
            </div>
          </div>
          <div style="display:flex;gap:8px;margin-bottom:7px;flex-wrap:wrap;">
            <span style="background:#fef3c7;color:#92400e;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:600;">🔥 ${meal.calories} kcal</span>
            <span style="background:#dbeafe;color:#1e40af;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:600;">💪 ${meal.protein}g proteină</span>
            <span style="background:#f3e8ff;color:#6b21a8;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:600;">⏱ ${meal.cooking_method}</span>
          </div>
          <div style="margin-bottom:5px;">
            <div style="font-size:9px;font-weight:600;color:#374151;margin-bottom:3px;">INGREDIENTE:</div>
            <div style="display:flex;flex-wrap:wrap;gap:3px;">
              ${meal.ingredients.map(i => `<span style="background:#dcfce7;color:#166534;padding:1px 5px;border-radius:8px;font-size:9px;">${i}</span>`).join('')}
            </div>
          </div>
          ${meal.benefits ? `<div style="font-size:9px;color:#6b7280;font-style:italic;border-top:1px solid #d1fae5;padding-top:5px;margin-top:5px;">${meal.benefits}</div>` : ''}
        </div>`;

      const dayBlockHtml = (d: Day) => `
        <div style="background:#fff;border-radius:12px;box-shadow:0 2px 6px rgba(0,0,0,0.07);overflow:hidden;">
          <div style="background:linear-gradient(135deg,#16a34a,#2563eb);padding:12px 16px;display:flex;align-items:center;gap:10px;">
            <div style="width:34px;height:34px;background:rgba(255,255,255,0.25);border-radius:9px;display:flex;align-items:center;justify-content:center;font-size:15px;font-weight:800;color:#fff;">${d.day}</div>
            <div>
              <div style="font-size:14px;font-weight:700;color:#fff;">Ziua ${d.day}</div>
              <div style="font-size:10px;color:rgba(255,255,255,0.85);">Total: ${d.breakfast.calories + d.lunch.calories + d.dinner.calories} kcal · Proteină: ${d.breakfast.protein + d.lunch.protein + d.dinner.protein}g</div>
            </div>
          </div>
          <div style="padding:12px 16px;">
            ${mealBlockHtml(d.breakfast, 'Mic Dejun', '🌅')}
            ${mealBlockHtml(d.lunch, 'Prânz', '☀️')}
            ${mealBlockHtml(d.dinner, 'Cină', '🌙')}
          </div>
        </div>`;

      const footerHtml = `
        <div style="text-align:center;padding:14px;color:#9ca3af;font-size:10px;border-top:1px solid #e5e7eb;">
          Generat de <strong style="color:#16a34a;">FII FIT</strong> · Plan nutrițional personalizat cu AI · ${dateStr}
        </div>`;

      // ── Randăm fiecare bloc separat ─────────────────────────────────────────
      const blocks: Array<{ canvas: HTMLCanvasElement; label: string }> = [];

      blocks.push({ canvas: await renderBlockToCanvas(headerHtml), label: 'header' });
      if (recHtml) blocks.push({ canvas: await renderBlockToCanvas(recHtml), label: 'rec' });
      if (shoppingHtml) blocks.push({ canvas: await renderBlockToCanvas(shoppingHtml), label: 'shopping' });
      for (const d of planData2.days) {
        blocks.push({ canvas: await renderBlockToCanvas(dayBlockHtml(d)), label: `day-${d.day}` });
      }
      blocks.push({ canvas: await renderBlockToCanvas(footerHtml), label: 'footer' });

      // ── Construim PDF cu smart page breaking ────────────────────────────────
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const PAGE_W = 210;   // mm
      const PAGE_H = 297;   // mm
      const MARGIN = 10;    // mm margine stânga/dreapta/sus/jos
      const CONTENT_W = PAGE_W - MARGIN * 2;  // 190mm
      const CONTENT_H = PAGE_H - MARGIN * 2;  // 277mm
      const GAP = 4;        // mm spațiu între blocuri

      // Scala: 1px în canvas (la scale=2) → câți mm în PDF
      // Canvas width = 754px * 2 = 1508px → CONTENT_W mm
      const PX_TO_MM = CONTENT_W / (754 * 2);

      let curY = MARGIN; // poziția curentă pe pagina curentă (mm)
      let isFirstPage = true;

      for (const block of blocks) {
        const blockHeightMM = block.canvas.height * PX_TO_MM;

        // Dacă blocul nu încape pe pagina curentă → pagină nouă
        // (excepție: dacă blocul e mai mare decât o pagină întreagă, îl punem oricum)
        const spaceLeft = CONTENT_H - (curY - MARGIN);
        if (!isFirstPage && blockHeightMM > spaceLeft && blockHeightMM <= CONTENT_H) {
          pdf.addPage();
          curY = MARGIN;
        }

        // Dacă blocul e mai mare decât o pagină, îl împărțim în felii
        if (blockHeightMM > CONTENT_H) {
          let sliceStartPx = 0;
          let remainingPx = block.canvas.height;

          while (remainingPx > 0) {
            const spaceLeftMM = CONTENT_H - (curY - MARGIN);
            const sliceHeightMM = Math.min(spaceLeftMM, blockHeightMM - sliceStartPx * PX_TO_MM);
            const sliceHeightPx = Math.ceil(sliceHeightMM / PX_TO_MM);

            const sliceCanvas = document.createElement('canvas');
            sliceCanvas.width = block.canvas.width;
            sliceCanvas.height = Math.min(sliceHeightPx, remainingPx);
            const ctx = sliceCanvas.getContext('2d');
            if (ctx) {
              ctx.drawImage(block.canvas, 0, sliceStartPx, block.canvas.width, sliceCanvas.height, 0, 0, block.canvas.width, sliceCanvas.height);
            }
            const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.92);
            const actualSliceHeightMM = sliceCanvas.height * PX_TO_MM;
            pdf.addImage(sliceImgData, 'JPEG', MARGIN, curY, CONTENT_W, actualSliceHeightMM);

            sliceStartPx += sliceCanvas.height;
            remainingPx -= sliceCanvas.height;
            curY += actualSliceHeightMM + GAP;

            if (remainingPx > 0) {
              pdf.addPage();
              curY = MARGIN;
            }
          }
        } else {
          // Bloc normal: adaugă imaginea la poziția curentă
          const imgData = block.canvas.toDataURL('image/jpeg', 0.92);
          pdf.addImage(imgData, 'JPEG', MARGIN, curY, CONTENT_W, blockHeightMM);
          curY += blockHeightMM + GAP;
        }

        isFirstPage = false;
      }

      // ── Salvare ─────────────────────────────────────────────────────────────
      const isCapacitor = !!(window as any).Capacitor?.isNativePlatform?.();

      if (isCapacitor) {
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
        const shareApi = navigator as any;
        const canShareFiles = typeof shareApi.canShare === 'function' && shareApi.canShare({ files: [pdfFile] });
        if (canShareFiles) {
          await shareApi.share({
            title: `Plan Nutrițional ${plan.days} Zile - FII FIT`,
            text: 'Plan nutrițional personalizat generat cu FII FIT',
            files: [pdfFile]
          });
        } else {
          pdf.save(fileName);
        }
      } else {
        pdf.save(fileName);
      }
    } catch (error) {
      console.error('Eroare la generarea PDF:', error);
      alert('A apărut o eroare la generarea PDF-ului. Încearcă din nou.');
    } finally {
      setExportingPDF(false);
    }
  };

  const renderMeal = (meal: Meal, type: string, icon: any) => {
    const Icon = icon;
    return (
      <div className="bg-white rounded-xl p-4 border-2 border-gray-100 hover:border-green-300 transition-all">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-xs text-gray-500">{type}</p>
            <h4 className="font-bold text-gray-900">{meal.name}</h4>
          </div>
        </div>

        <div className="space-y-3">
          {/* Cooking Method */}
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">Metodă: <span className="font-medium capitalize">{meal.cooking_method}</span></span>
          </div>

          {/* Calories & Protein */}
          <div className="flex gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-gray-600">{meal.calories} kcal</span>
            </div>
            <div className="text-sm text-gray-600">
              Proteine: {meal.protein}g
            </div>
          </div>

          {/* Ingredients */}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-2">Ingrediente:</p>
            <div className="flex flex-wrap gap-2">
              {meal.ingredients.map((ing, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                >
                  {ing}
                </span>
              ))}
            </div>
          </div>

          {/* Benefits */}
          {meal.benefits && (
            <div className="pt-2 border-t border-gray-100">
              <p className="text-xs text-gray-600 italic">{meal.benefits}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 print:bg-white">
      {/* Transfer Date Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Transferă Ziua {showTransferModal.dayNumber}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Alege data pentru care vrei să transferi mesele în Mese & Calorii:
            </p>
            <input
              type="date"
              value={transferDate}
              onChange={e => setTransferDate(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowTransferModal(null)}
                className="flex-1 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-sm hover:bg-gray-50"
              >
                Anulează
              </button>
              <button
                onClick={() => handleTransferDay(showTransferModal.dayNumber, transferDate)}
                className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-semibold text-sm hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <ArrowRightCircle className="w-4 h-4" />
                Transferă
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="print:hidden">
        <PageHeader
          title={`Plan ${plan.days} Zile`}
          subtitle={`Generat pe ${new Date(plan.createdAt).toLocaleDateString('ro-RO')}`}
          showBack={true}
          backTo="/nutrition"
          icon={<ChefHat className="w-5 h-5" />}
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowShoppingList(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 transition-all font-semibold"
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="hidden sm:inline">Listă Cumpărături</span>
              </button>
              <button
                onClick={handleExportPDF}
                disabled={exportingPDF}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
              >
                {exportingPDF ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    <span className="hidden sm:inline">Generez PDF...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Export PDF</span>
                  </>
                )}
              </button>
            </div>
          }
        />
      </div>

      {/* Print Header */}
      <div className="hidden print:block p-6 border-b">
        <h1 className="text-2xl font-bold text-gray-900">Plan Nutrițional - {plan.days} Zile</h1>
        <p className="text-sm text-gray-600">Generat: {new Date(plan.createdAt).toLocaleDateString('ro-RO')}</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 print:px-8">
        {/* Recommendations */}
        {planData.recommendations && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 mb-6 print:border print:border-green-200">
            <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
              <ChefHat className="w-5 h-5" />
              Recomandări Generale
            </h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              {planData.recommendations}
            </p>
          </div>
        )}

        {/* Shopping List Banner */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-2xl p-5 mb-6 print:hidden">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm">
                <ShoppingCart className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Listă de Cumpărături</h3>
                <p className="text-sm text-gray-500">
                  Organizată pe categorii · Export PDF sau WhatsApp
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowShoppingList(true)}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm"
            >
              <ShoppingCart className="w-4 h-4" />
              Generează Lista
            </button>
          </div>
        </div>

        {/* Shopping List (print only) */}
        {planData.shopping_list && planData.shopping_list.length > 0 && (
          <div className="hidden print:block bg-white rounded-2xl shadow-xl p-6 mb-6 print:break-inside-avoid">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Listă de Cumpărături</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {planData.shopping_list.map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg text-sm"
                >
                  <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                  <span className="text-gray-900">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Toggle All Button + Days */}
        <div className="flex items-center justify-between mb-4 print:hidden">
          <h3 className="text-lg font-bold text-gray-900">Mesele pe zile</h3>
          <button
            onClick={toggleAllDays}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-green-200 text-green-700 rounded-xl hover:bg-green-50 hover:border-green-400 transition-all font-semibold text-sm shadow-sm"
          >
            {allExpanded ? (
              <>
                <ChevronsDownUp className="w-5 h-5" />
                Închide toate zilele
              </>
            ) : (
              <>
                <ChevronsUpDown className="w-5 h-5" />
                Deschide toate zilele
              </>
            )}
          </button>
        </div>

        <div className="space-y-4">
          {currentPlanData.days.map((dayData) => (
            <div
              key={dayData.day}
              className="bg-white rounded-2xl shadow-xl overflow-hidden print:break-inside-avoid print:mb-6"
            >
              {/* Day Header */}
              <div className="flex items-center print:pointer-events-none">
                <button
                  onClick={() => toggleDay(dayData.day)}
                  className="flex-1 p-6 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {dayData.day}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Ziua {dayData.day}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {dayData.breakfast.calories + dayData.lunch.calories + dayData.dinner.calories} kcal total
                    </p>
                  </div>
                  <ArrowLeft className={`w-5 h-5 text-gray-400 transition-transform print:hidden ml-auto ${
                    expandedDays.has(dayData.day) ? 'rotate-90' : '-rotate-90'
                  }`} />
                </button>

                {/* Action buttons - Regenerate & Transfer */}
                <div className="flex items-center gap-2 pr-4 print:hidden">
                  {/* Regenerate day button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleRegenerateDay(dayData.day); }}
                    disabled={regeneratingDay === dayData.day}
                    title="Regenerează ziua"
                    className="flex items-center gap-1.5 px-3 py-2 bg-orange-50 border border-orange-200 text-orange-600 rounded-xl text-xs font-semibold hover:bg-orange-100 disabled:opacity-50 transition-all"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${regeneratingDay === dayData.day ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">
                      {regeneratingDay === dayData.day ? 'Regenerez...' : 'Regenerează'}
                    </span>
                  </button>

                  {/* Transfer to food tracking button */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowTransferModal({ dayNumber: dayData.day }); }}
                    disabled={transferringDay === dayData.day}
                    title="Transferă în Mese & Calorii"
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all border ${
                      transferredDays.has(dayData.day)
                        ? 'bg-green-50 border-green-200 text-green-600'
                        : 'bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100'
                    } disabled:opacity-50`}
                  >
                    {transferredDays.has(dayData.day) ? (
                      <><CheckCircle2 className="w-3.5 h-3.5" /><span className="hidden sm:inline">Transferat</span></>
                    ) : transferringDay === dayData.day ? (
                      <><div className="w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" /><span className="hidden sm:inline">Transfer...</span></>
                    ) : (
                      <><CalendarPlus className="w-3.5 h-3.5" /><span className="hidden sm:inline">Transferă</span></>
                    )}
                  </button>
                </div>
              </div>

              {(expandedDays.has(dayData.day) || typeof window !== 'undefined' && window.matchMedia('print').matches) && (
                <div className="px-6 pb-6 space-y-4">
                  {renderMeal(dayData.breakfast, 'Mic Dejun', Utensils)}
                  {renderMeal(dayData.lunch, 'Prânz', ChefHat)}
                  {renderMeal(dayData.dinner, 'Cină', Utensils)}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Shopping List Modal */}
      <ShoppingListModal
        isOpen={showShoppingList}
        onClose={() => setShowShoppingList(false)}
        planData={currentPlanData}
        planDays={plan.days}
        planDate={plan.createdAt}
      />
    </div>
  );
}
